/** WEEX v3 wire decoding shared by the legacy page and SDK subscriptions.
 * @see https://www.weex.com/api-doc/spot/Websocket/public/Tickers-Channel
 */
// The channel-specific DTOs are checked at the subscription boundary; this decoder
// also supports the array ticker envelope observed on the live v3 service.

export function normalizeWeexMessage(wire: any, depth = 200): any {
  const rows: Record<string, any>[] = Array.isArray(wire.d) ? wire.d : wire.d ? [wire.d] : [];
  const symbol = wire.s;
  if (wire.e === 'ticker' || wire.e === '24hrTicker') {
    return {
      channel: `${symbol}@ticker`,
      data: rows.map(row => ({
        symbol,
        lastPrice: row.c,
        high: row.h,
        low: row.l,
        open: row.o,
        size: row.v,
        value: row.q,
        priceChange: row.p,
        priceChangePercent: weexChangePercent(row.c, row.o, row.P),
        ts: wire.E,
      })),
    };
  }
  if (wire.e === 'depth' || wire.e === 'depthSnapshot') {
    if (!Array.isArray(wire.a) || !Array.isArray(wire.b)) return null;
    return {
      channel: `${symbol}@depth${wire.l ?? depth}`,
      data: [
        {
          symbol,
          depthType: wire.e === 'depthSnapshot' ? 'SNAPSHOT' : wire.d,
          asks: wire.a.map(([price, size]: string[]) => ({ price, size })),
          bids: wire.b.map(([price, size]: string[]) => ({ price, size })),
          ts: wire.E,
        },
      ],
    };
  }
  if (wire.e === 'trade' || wire.e === 'tradeSnapshot') {
    return {
      channel: `${symbol}@trade`,
      data: rows.map(row => ({
        time: String(row.T),
        price: row.p,
        size: row.q,
        buyerMaker: row.m,
      })),
    };
  }
  if (wire.e === 'kline' && rows.length) {
    return {
      channel: `${symbol}@kline_${rows[0].i}_${wire.p ?? 'LAST_PRICE'}`,
      data: rows.map(row => [row.t, row.o, row.h, row.l, row.c, row.v, row.q, row.T]),
    };
  }
  return null;
}

/** Reply to both documented and observed v3 keepalive frames. */
export function weexPong(wire: { event?: string; type?: string; id?: number }) {
  return wire.event === 'ping' || wire.type === 'ping'
    ? { method: 'PONG', id: wire.id ?? 1 }
    : null;
}

/** Track depth continuity. After reconnect or a sequence gap, require a fresh snapshot. */
export class WeexDepthSequence {
  private last: number | undefined;
  private snapshot = false;

  reset() {
    this.last = undefined;
    this.snapshot = false;
  }

  accept(wire: { e?: string; d?: unknown; U?: number; u?: number }): 'accept' | 'skip' | 'gap' {
    if (wire.e !== 'depth' && wire.e !== 'depthSnapshot') return 'accept';
    if (wire.e === 'depthSnapshot' || wire.d === 'SNAPSHOT') {
      this.snapshot = true;
      this.last = wire.u;
      return 'accept';
    }
    if (!this.snapshot) return 'skip';
    if (this.last != null && wire.u != null && wire.u <= this.last) return 'skip';
    if (this.last != null && wire.U != null && wire.U > this.last + 1) {
      this.reset();
      return 'gap';
    }
    this.last = wire.u;
    return 'accept';
  }
}

/** Derive percentage from prices because live v3 reports a fractional P despite its docs. */
export function weexChangePercent(
  last: string,
  open: string | undefined,
  reported: string,
): string {
  const opening = Number(open);
  const closing = Number(last);
  return opening > 0 && Number.isFinite(opening) && Number.isFinite(closing)
    ? String(Number((((closing - opening) / opening) * 100).toPrecision(12)))
    : reported;
}
